import { useAppUserAccess } from 'global/cnr/contexts/useHelpers/useAppUserAccess';
import _ from 'lodash';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { List, Segment } from 'semantic-ui-react';
import { DisplayContext } from '../../../global/cnr/contexts/DisplayContext';
import { ParentContext } from '../../../global/cnr/contexts/ParentContext';
import { uniqueKey } from '../../../global/common/keys';
import NoData from '../../../global/components/alerts/NoData';
import UiSaveButtons from '../../../global/components/buttons/UiSaveButtons';
import { SimpleDnD } from '../../../global/dragNDrops/SimpleDnD';
import { appIconTypes } from '../../../global/enums/appIconTypes';
import GenericIcon from '../../../global/genericControls/GenericIcon';
import { H3Wrapper } from '../../../global/wrappers/TopperWrappers';
import Wrapper, { wrapperTypes } from '../../../global/wrappers/Wrapper';
import SportsEditMatchProvider, { SportsEditMatchContext } from '../cnr/contexts/SportsEditMatchContext';
import SportsMatchProvider, { SportsMatchContext } from '../cnr/contexts/SportsMatchContext';
import SportsRtMatchProvider, { SportsRtMatchContext } from '../cnr/contexts/SportsRtMatchContext';
import { SportsSeasonContext } from '../cnr/contexts/SportsSeasonContext';
import { gameScoringTypes, matchUpdateTypes } from '../cnr/reducers/SportsSeasonReducer';
import SportsAthletes from '../elements/athletes/SportsAthletes';
import { _sdm } from '../elements/dashboard/SportsDashboard';
import MatchDataUpdate from '../elements/matches/MatchDataUpdate';
import MatchDetailsUpdate from '../elements/matches/MatchDetailsUpdate';
import MatchItemScoring from '../elements/matches/MatchItemScoring';
import MatchScoreTable from '../elements/matches/MatchScoreTable';
import { matchCalcs } from '../helpers/match';
import { useMatchUpdate } from '../useHelpers/useMatchUpdate';
import { _allowSportsUpdates } from '../useHelpers/useSeasonals';
import MatchScoreButtons from '../elements/matches/MatchScoreButtons';
import { useRealtimeResults, useRealtimeUpdates } from '../useHelpers/useRealtimeResults';

const stepTypes = {
  enterScores: 'enterScores',
  pickTopPlayers: 'pickTopPlayers',
  sortTopPlayers: 'sortTopPlayers',
  confirmResult: 'confirmResult',
}

const _scoreTypes = {
  gameScore: 'gameScore',
  matchScore: 'matchScore',
  setScores: 'setScores',
  halfScores: 'halfScores',
  quarterScores: 'quarterScores',
}

const CreateMatchResult = (props) => {

  const { realTimeOnly, includePlayers, matchResultType } = props ?? {}



  const appUserAccess = useAppUserAccess()
  const { isSuperAdminDeveloper } = appUserAccess ?? {}

  const parentContext = useContext(ParentContext)
  const { states, fns } = parentContext ?? {}
  const { appUser_state } = states ?? {}
  const { appUser_fns } = fns ?? {}
  const { accessClone } = appUser_state ?? {}

  // displayContext
  const displayContext = useContext(DisplayContext);

  // sportsSeasonContext
  const sportsSeasonContext = useContext(SportsSeasonContext)
  const { sportsSeason_state, sportsSeason_handlers } = sportsSeasonContext
  const { matches_info, currentSeason_info, sportsKey, sportsShowOptions, sportPermissions, sportDataSettings } = sportsSeason_state ?? {}
  const { gameScoringType } = currentSeason_info ?? {}
  const { useRtGameDayData } = sportDataSettings ?? {}
  const { matchKey: matchKey_sso } = sportsShowOptions ?? {}

  const { updateMatchResults, updating: updating_match } = useMatchUpdate()
  const { updateMatchResults_matchDay, updating: updating_matchDay } = useRealtimeUpdates({ currentSeason_info })

  const updating = updating_match || updating_matchDay

  // sportsMatchContext
  const sportsMatchContext = useContext(SportsMatchContext)
  const { sportsMatch_state } = sportsMatchContext ?? {}
  const { match_true, editFromRt } = sportsMatch_state ?? {}

  // sportsRtMatchContext
  const sportsEditMatchContext = useContext(SportsEditMatchContext)
  const { sportsEditMatch_state, sportsEditMatch_handlers } = sportsEditMatchContext ?? {}
  const { match_current, match_edit, matchPendingUpdate, sortedAthletes } = sportsEditMatch_state ?? {}
  const { handleShow_matchPending } = sportsEditMatch_handlers ?? {}
  const { matchUpdateType } = matchPendingUpdate ?? {}
  const { _results: _results_edit } = match_edit ?? {}
  const { _results: _results_current } = match_current ?? {}

  const _results = _results_edit || _results_current
  const { complete: complete_true } = _results ?? {}

  // sportsRtMatchContext
  const sportsRtMatchContext = useContext(SportsRtMatchContext)
  const { sportsRtMatch_state } = sportsRtMatchContext ?? {}
  const { match_rt } = sportsRtMatch_state ?? {}

  const { match_complete_possible: mcp_rt } = match_rt ?? {}
  const componentContexts = { displayContext }

  const _allowMatchUpdate = _allowSportsUpdates.matches && sportPermissions?.any ? true : false


  let cst = editFromRt ? _scoreTypes.setScores : _scoreTypes.matchScore

  const [steps, setSteps] = useState()
  const [matchResults, setMatchResults] = useState()
  const [matchIsComplete, setMatchIsComplete] = useState()
  const [currentScoreType] = useState(matchResultType || cst)
  const [step, setStep] = useState()

  const { complete, match_complete_possible: mcp_edit } = matchResults ?? {}

  const stepCount = steps ? steps.length : 0

  const _updateMatchCaption = useMemo(() => {
    if (isSuperAdminDeveloper) {
      return 'Update Official'
    } else {
      return useRtGameDayData ? 'Update Match Day *' : 'Update Match Day *'
    }
  }, [isSuperAdminDeveloper])

  const updateRtGameDayData = useMemo(() => {
    if (!isSuperAdminDeveloper) {
      return false
    } else {
      return true
    }
  }, [isSuperAdminDeveloper])

  const matchAllows = useMemo(() => {
    const vaProps = { currentMatch: match_edit, sportsKey, accessClone }
    const _matchAllows = appUser_fns.validateAccess_allowScoring(vaProps)
    return _matchAllows
  }, [match_edit])

  useEffect(() => {
    if (match_edit) {
      setMatchResults(_results)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [match_edit]);

  useEffect(() => {
    let _steps;

    if (editFromRt) {
      _steps = [stepTypes.enterScores, stepTypes.confirmResult]
    } else {
      switch (gameScoringType) {
        case gameScoringTypes.match:
        case gameScoringTypes.matchPoints:
          _steps = includePlayers ?
            [stepTypes.enterScores, stepTypes.pickTopPlayers, stepTypes.sortTopPlayers, stepTypes.confirmResult]
            :
            [stepTypes.enterScores, stepTypes.confirmResult]
          break;

        case gameScoringTypes.half:
          _steps = includePlayers ?
            [stepTypes.enterScores, stepTypes.pickTopPlayers, stepTypes.sortTopPlayers, stepTypes.confirmResult]
            :
            [stepTypes.enterScores, stepTypes.confirmResult]
          break;

        case gameScoringTypes.quarter:
          _steps = includePlayers ?
            [stepTypes.enterScores, stepTypes.pickTopPlayers, stepTypes.sortTopPlayers, stepTypes.confirmResult]
            :
            [stepTypes.enterScores, stepTypes.confirmResult]
          break;

        default:
          _steps = includePlayers ?
            [stepTypes.enterScores, stepTypes.pickTopPlayers, stepTypes.sortTopPlayers, stepTypes.confirmResult]
            :
            [stepTypes.enterScores, stepTypes.confirmResult]
          break;
      }
    }
    setSteps(_steps)
    setStep({ index: 0, name: _steps[0] })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchIsComplete]);

  useEffect(() => {
    setMatchIsComplete(complete_true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [complete_true]);

  useEffect(() => {
    if (editFromRt && match_rt) {
      const res = { score: match_rt.score, scores: match_rt.scores }
      sportsEditMatch_handlers.handleAmmend_resultsCurrent(res)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUpdate_matchResult = () => {
    const cb = (results) => { }
    if (isSuperAdminDeveloper) {
      updateMatchResults({ match_edit, callback: cb, updateRtGameDayData })
    } else {
      updateMatchResults_matchDay({ match_edit, callback: cb })
    }
  }

  const handleUpdate_matchResult_matchDay = () => {
    const cb = (results) => { }
    updateMatchResults_matchDay({ match_edit, callback: cb })
  }

  const validateComponent = () => {
    switch (gameScoringType) {
      case gameScoringTypes.match:
      case gameScoringTypes.matchPoints:
        switch (step.name) {
          case stepTypes.enterScores:
            return complete || mcp_edit || mcp_rt
          case stepTypes.pickTopPlayers:
            return true
          case stepTypes.sortTopPlayers:
            return true
          case 3:
          case stepTypes.confirmResult:
            return true
          default:
            break;
        }
        break;

      case gameScoringTypes.half:
      case gameScoringTypes.quarter:
        return true

      default:
      // nothing
    }
  }

  const allowBack = step && step.index > 0
  const allowForward = step && (step.index < (stepCount - 1) && validateComponent())

  const handleUpdatedList = (l) => sportsEditMatch_handlers.handleSelect_athletes(l.data)

  const footer_match = () => {
    const btns = []
    btns.push({
      oc: handleUpdate_matchResult,
      caption: _updateMatchCaption,
      icon: appIconTypes.save,
      color: _allowMatchUpdate ? 'green' : 'teal'
    })
    if (isSuperAdminDeveloper) {
      btns.push({
        oc: handleUpdate_matchResult_matchDay,
        caption: `Update Match Day`,
        icon: appIconTypes.save,
        color: 'teal',
        fl: true
      })
    }
    return <UiSaveButtons others={btns} />

  }

  const playerItem = (player) => {
    const { lastName, firstName, teams, uniformNumber, playerPosition } = player
    return <List.Item>
      <List.Content>
        <List.Header>{firstName}{' '}{lastName}</List.Header>
        <List.Description>{'#'}{uniformNumber}{' - '}{teams}</List.Description>
        <List.Description>{playerPosition}</List.Description>
      </List.Content>
    </List.Item>
  }

  const playerList = () => {
    if (sortedAthletes) {
      const list = sortedAthletes.map(sa => (playerItem(sa)))
      return <List divided relaxed ordered>
        {list}
      </List>
    } else {
      return 'None'
    }
  }

  const confirmContent = () => <React.Fragment>
    <Segment.Group>
      <Segment color={'blue'}>{'Result'}</Segment>
      <Segment><MatchScoreTable /></Segment>
    </Segment.Group>
    {includePlayers && <Segment.Group>
      <Segment color={'blue'}>{'Top Players'}</Segment>
      <Segment>{playerList()}</Segment>
    </Segment.Group>}
  </React.Fragment>

  const match_score = () => <MatchItemScoring key={uniqueKey('crm', 'ms', match_edit.key)} />
  const match_scores = () => <MatchItemScoring key={uniqueKey('crm', 'mss', match_edit.key)} setScoring={true} />
  const match_rtScores = () => <MatchScoreButtons />

  const match_players = () => <SportsAthletes componentContexts={componentContexts} />
  const match_sortPlayers = () => sortedAthletes ? <SimpleDnD dataItems={sortedAthletes} handleUpdatedList={handleUpdatedList} /> : <Segment basic>{'No players selected'}</Segment>

  const match_confirm = () => <Wrapper
    content={confirmContent()}
    footer={footer_match()}
    wrapperType={wrapperTypes.padded}
  />

  const scoringContainer = () => {
    switch (gameScoringType) {
      case gameScoringTypes.match:
      case gameScoringTypes.matchPoints:
        if (currentScoreType === _scoreTypes.setScores) {
          return match_scores()
        } else {
          return match_score()
        }
      case gameScoringTypes.half:
        return match_scores()
      case gameScoringTypes.quarter:
        return match_scores()
      default:
        break;
    }
  }

  const handleStep = (co) => {
    const { count } = co
    let newStep = step.index + count
    if (newStep < 0) { newStep = 0 }
    if (newStep > (stepCount - 1)) { newStep = stepCount - 1 }
    const name = steps[newStep];
    setStep({ index: newStep, name })
  }

  const gi = (key, iconName, onClick, clickOptions, opts, corner) => <GenericIcon giProps={{ key, iconName, onClick, clickOptions, opts, corner }} />

  const buttonProps_left = { color: 'black', inverted: true }
  const buttonProps_right = { color: 'green', inverted: true }

  const leftButton = () => gi('btn-back', appIconTypes.arrowLeft, handleStep, { count: -1 }, { ...buttonProps_left, nb: false, nf: true, disabled: !allowBack })
  const rightButton = () => gi('btn-forward', appIconTypes.arrowRight, handleStep, { count: 1 }, { cn: allowForward ? 'blink_me_g' : null, ...buttonProps_right, nb: false, nf: true, disabled: !allowForward })


  const wizardHeader = () => <H3Wrapper
    leftContent={leftButton()}
    middleContent={_.startCase(step.name)}
    rightContent={rightButton()}
    widths={['22%', '56%', '22%']}
  />

  const scoresContent = () => {
    switch (step.name) {
      case stepTypes.enterScores:
        return scoringContainer()
      case stepTypes.pickTopPlayers:
        return match_players()
      case stepTypes.sortTopPlayers:
        return match_sortPlayers()
      case stepTypes.confirmResult:
        return match_confirm()
      default:
        break;
    }
  }

  const wizard = () => <Wrapper
    header={wizardHeader()}
    content={scoresContent()}
  />

  // const content_direct = () => {
  //   return scoringContainer()
  // }

  const content = () => {
    if (matchPendingUpdate) {
      switch (matchUpdateType) {
        case matchUpdateTypes.updateMatchListResultsFromGs:
          return <MatchDataUpdate sportsDataMode={_sdm.sports.seasonResults} handleCancel={handleShow_matchPending} matchUpdateType={matchUpdateType} fromApp={true} />
        default:
          return <MatchDetailsUpdate handleCancel={handleShow_matchPending} matchAllows={matchAllows} />
      }
    } else if (realTimeOnly) {
      return match_scores()
    } else {
      return wizard()
    }
  }

  const content_results = () => {
    if (matchAllows) {
      if (matchAllows.edit) {
        return content()
      } else if (matchAllows.scores || matchAllows.scoresRt) {
        return content()
      } else {
        return <div>NSA</div>
      }
    } else {
      return <div>NSA</div>
    }
  }

  const wrapper = () => <Wrapper
    content={content_results()}
    wrapperType={wrapperTypes.paddedFooter}
    updating={updating}
    css={{ container: 'app-dark-mode' }}
  />

  return step && match_edit ? wrapper() : <div>No Match Key</div>

}

export default CreateMatchResult

export const CreateMatchResult_withProvider = (props) => {
  return <SportsMatchProvider {...props}>
    <SportsEditMatchProvider {...props}>
      <SportsRtMatchProvider {...props}>
        <CreateMatchResult {...props} />
      </SportsRtMatchProvider>
    </SportsEditMatchProvider>
  </SportsMatchProvider>
} 